import { VoteGroupData, VoteOption } from 'types';
import { groupBy } from 'lodash-es';

export const getVoteOptionsList = (
  data: VoteGroupData[],
  voteChoices: VoteOption[],
  totalRounds: number
): VoteOption[] => {
  const groupCountPerRound = getCountPerRound(data);

  // Keep count of round's rendered groups to determine in which grid region option should be rendered
  // The first half should be rendered in region 1. The second half should be rendered in region 2
  const roundsIndexCount: Record<number, any> = {};

  // Map through groups and get vote options and UI settings
  return data
    .map((voteGroup: VoteGroupData) => {
      const { vote_options: voteOptions, round_id: roundId, ...voteGroupData } = voteGroup;

      // Update round index pointer
      roundsIndexCount[roundId] = (roundsIndexCount[roundId] || 0) + 1;

      // Get round's half point
      const half = groupCountPerRound[roundId - 1] / 2;

      const isFinalVotingRound = roundId === totalRounds;
      const isWinnerRound = roundId === totalRounds + 1;

      // Return list of vote option with UI settings
      return createVoteOptionsList(
        voteOptions as VoteOption[],
        voteChoices,
        voteGroupData,
        roundId,
        isFinalVotingRound,
        isWinnerRound,
        half,
        roundsIndexCount
      );
    })
    .flat();
};

const createVoteOptionsList = (
  voteOptions: VoteOption[],
  voteChoices: VoteOption[],
  voteGroupData: Partial<VoteGroupData>,
  roundId: number,
  isFinalRound: boolean,
  isWinnerRound: boolean,
  half: number,
  roundsIndexCount: Record<number, any>
) => {
  return voteOptions.reduce((acc: any, voteOption: VoteOption, voteOptionIndex: number) => {
    const roundCount = roundsIndexCount[roundId];

    // Check to which region vote option should go
    const region = isFinalRound ? voteOptionIndex + 1 : roundCount > half ? 2 : 1;

    // Get vote option UI settings
    const match = region === 1 ? roundCount : roundCount - half;
    const team = voteOptionIndex + 1;
    const position = isFinalRound ? 1 : match * 2 - (team % 2);

    // Don't add winner round to vote options list. Voting is disabled on winne round.
    // On final round, split vote group in 2 regions
    // Region 1 to render team 1 and Region 2 to render team 2
    if (isWinnerRound || (isFinalRound && region !== team)) {
      return acc;
    }

    const voteOptionData = voteChoices.find((v) => v.schema_reference === voteOption.referenceId) || {};

    const option = {
      ...voteOption,
      ...voteGroupData,
      ...voteOptionData,
      region,
      roundId,
      position,
      positionInMatchup: voteOption.position,
    };

    acc.push(option);
    return acc;
  }, []);
};

const getCountPerRound = (data: VoteGroupData[]) => {
  // Group voteGroups by round
  const groupsByRound = groupBy(data, 'round_id');
  // Get groups count per round
  return Object.values(groupsByRound).map((round: VoteGroupData[]) => round.length);
};
